export function TrueCostOfRemodeling() {
  const costs = [
    {
      category: 'Direct Renovation Costs',
      icon: 'ri-hammer-line',
      items: [
        { item: 'Kitchen upgrades per unit', cost: '$8,000 - $25,000' },
        { item: 'Bathroom renovations per unit', cost: '$6,000 - $18,000' },
        { item: 'Flooring replacement per unit', cost: '$3,000 - $8,000' },
        { item: 'Paint and cosmetic updates', cost: '$2,000 - $5,000' },
        { item: 'HVAC system upgrades', cost: '$15,000 - $50,000' },
        { item: 'Roof repairs or replacement', cost: '$25,000 - $150,000' },
        { item: 'Plumbing and electrical updates', cost: '$20,000 - $75,000' }
      ]
    },
    {
      category: 'Hidden & Carrying Costs',
      icon: 'ri-eye-off-line',
      items: [
        { item: 'Mortgage payments during renovation', cost: '$5,000 - $20,000/month' },
        { item: 'Property taxes during renovation', cost: '$2,000 - $10,000/month' },
        { item: 'Insurance premiums', cost: '$1,000 - $5,000/month' },
        { item: 'Utilities for vacant units', cost: '$500 - $3,000/month' },
        { item: 'Lost rental income', cost: '$10,000 - $50,000/month' },
        { item: 'Property management fees', cost: '$1,000 - $5,000/month' },
        { item: 'Unexpected cost overruns (typical)', cost: '15-30% of budget' }
      ]
    },
    {
      category: 'Professional Services',
      icon: 'ri-team-line',
      items: [
        { item: 'Architect and design fees', cost: '$10,000 - $50,000' },
        { item: 'General contractor markup', cost: '15-25% of project' },
        { item: 'Permits and inspections', cost: '$5,000 - $25,000' },
        { item: 'Engineering consultations', cost: '$5,000 - $20,000' },
        { item: 'Project management', cost: '$3,000 - $10,000/month' }
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The True Cost of Remodeling Your Apartment Building
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Most owners drastically underestimate renovation costs. Here's what you're really looking at when you choose to remodel before selling.
          </p>
        </div>

        {/* Cost Breakdown Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {costs.map((category, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                <i className={`${category.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold mb-6">{category.category}</h3>
              <div className="space-y-4">
                {category.items.map((item, idx) => (
                  <div key={idx} className="border-b border-gray-100 pb-3 last:border-0">
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-sm text-gray-700 flex-1">{item.item}</span>
                      <span className="text-sm font-bold text-red-600 whitespace-nowrap">{item.cost}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Example Calculation */}
        <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-10 border-2 border-red-200 mb-12">
          <h3 className="text-3xl font-bold mb-8 text-center">Real-World Example: 20-Unit Apartment Building</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-bold mb-4 text-red-600">Renovation Costs</h4>
              <div className="space-y-3 text-gray-700">
                <div className="flex justify-between">
                  <span>Unit renovations (20 units × $25,000)</span>
                  <strong>$500,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Common area upgrades</span>
                  <strong>$75,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Roof replacement</span>
                  <strong>$80,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>HVAC system upgrade</span>
                  <strong>$40,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Professional services & permits</span>
                  <strong>$45,000</strong>
                </div>
                <div className="flex justify-between pt-3 border-t-2 border-red-300">
                  <span className="font-bold">Direct Costs Subtotal</span>
                  <strong className="text-red-600">$740,000</strong>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4 text-red-600">Carrying Costs (12 months)</h4>
              <div className="space-y-3 text-gray-700">
                <div className="flex justify-between">
                  <span>Mortgage payments</span>
                  <strong>$120,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Property taxes</span>
                  <strong>$48,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Insurance</span>
                  <strong>$24,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Lost rental income</span>
                  <strong>$240,000</strong>
                </div>
                <div className="flex justify-between">
                  <span>Utilities & maintenance</span>
                  <strong>$36,000</strong>
                </div>
                <div className="flex justify-between pt-3 border-t-2 border-red-300">
                  <span className="font-bold">Carrying Costs Subtotal</span>
                  <strong className="text-red-600">$468,000</strong>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t-2 border-red-300">
            <div className="flex justify-between items-center text-2xl font-bold">
              <span>Total Investment Required</span>
              <span className="text-red-600">$1,208,000</span>
            </div>
            <p className="text-center mt-6 text-lg text-gray-700">
              That's over <strong className="text-red-600">$1.2 million</strong> you need to spend before you can even list the property!
            </p>
          </div>
        </div>

        {/* Warning Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/a1c4978c35cd7b760122be8c08d96aa9.jpg"
            alt="High costs of apartment building renovation"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}